import { IExchangeData } from '@src/types/exchange'
import { GasFeeSimulationResults } from '@src/types/gasFees'
import { PrecomputeGasResults, TransactionType } from '@src/types/gasPrices'
import { gasFeeslogger } from '@src/utils/logger'

export const approximateGasFeesEvm = (
    precomputedGasPrices: PrecomputeGasResults,
    precomputed_results: GasFeeSimulationResults[],
    sellData: IExchangeData,
    isCCTP: boolean,
    isNative: boolean,
    isApprovalNeeded: boolean,
    isWrapOrUnwrapOnly: boolean
): GasFeeSimulationResults => {
    try {
        if (!precomputedGasPrices[sellData.chainId]) {
            throw new Error(
                `[approximateGasFees] precomputedGasPrices does not contain data for chainId ${sellData.chainId}`
            )
        }

        if (isWrapOrUnwrapOnly) {
            precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.WRAP])
        } else {
            if (isCCTP) {
                if (isApprovalNeeded) {
                    precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE])
                }
                precomputed_results.push(
                    precomputedGasPrices[sellData.chainId].results[TransactionType.DEPOSIT_FOR_BURN]
                )
            } else {
                if (isNative) {
                    // Base operations needed for native swaps
                    precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.WRAP])
                    precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE])
                    precomputed_results.push(
                        precomputedGasPrices[sellData.chainId].results[TransactionType.PLACE_ORDER]
                    )

                    // Add operations 3 more times for a larger buffer (total 4x)
                    for (let i = 0; i < 3; i++) {
                        precomputed_results.push(precomputedGasPrices[sellData.chainId].results[TransactionType.WRAP])
                        precomputed_results.push(
                            precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE]
                        )
                        precomputed_results.push(
                            precomputedGasPrices[sellData.chainId].results[TransactionType.PLACE_ORDER]
                        )
                    }
                } else {
                    if (isApprovalNeeded) {
                        precomputed_results.push(
                            precomputedGasPrices[sellData.chainId].results[TransactionType.APPROVE]
                        )
                    }
                    precomputed_results.push(
                        precomputedGasPrices[sellData.chainId].results[TransactionType.PLACE_ORDER]
                    )
                }
            }
        }

        gasFeeslogger.info('precomputed_results = ', precomputed_results)

        const sneed = precomputed_results.find(g => g.totalFeePerGasUnit > BigInt(0))
        const nonZeroTotalFeePerGasUnit = sneed ? sneed.totalFeePerGasUnit : BigInt(0)

        const result = {
            totalGasUnits: precomputed_results.reduce((acc, val) => {
                if (val && val.totalGasUnits) {
                    return acc + val.totalGasUnits
                } else {
                    return acc
                }
            }, BigInt(0)),
            individualGasUnits: precomputed_results.map(o => {
                if (o.individualGasUnits) {
                    return o.individualGasUnits[0]
                } else {
                    return BigInt(0)
                }
            }),
            totalFeePerGasUnit: nonZeroTotalFeePerGasUnit,
            feeMetrics: precomputedGasPrices[sellData.chainId].feeMetrics,
        } as GasFeeSimulationResults

        gasFeeslogger.info('Returning result:', result)

        return result
    } catch (err) {
        gasFeeslogger.error('Cannot approximate EVM gas fees', { error: err })
        return {
            totalGasUnits: BigInt(0),
            individualGasUnits: [BigInt(0)],
            totalFeePerGasUnit: BigInt(0),
            feeMetrics: {},
        } as GasFeeSimulationResults
    }
}
